import React, { useCallback, useEffect, useMemo } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import {
  CasinoGamesProvider,
  CategoryGamesProvider,
  isEmptyCollection,
  useApplicationState,
  useCasinoGamesState,
  useCasinoNewState,
} from '@atlas/core';
import { EmptyStateView, HeaderTitle, Icon, Loader } from '@atlas/ui';

import { useCasinoFilterState } from 'components/Casino/CasinoFilterContext';
import { CasinoSearchCategoryGames } from 'components/Casino/CasinoGamesList';
import { MIN_SEARCH_COUNT } from 'components/Casino/components/CasinoSearch';
import GamesInfiniteLoader from 'components/Casino/components/GamesInfiniteLoader';
import CasinoLoadMoreButton from 'src/components/Casino/components/CasinoLoadMoreButton';

const DEFAULT_FILTER_VALUE = [];
const SEARCH_GAMES_COUNT = 10;

const sliderSettings = { rows: 1, size: 'L' };

const GamesSearchResults = ({ setGamesLoading }) => {
  const { currentSlice, games, hasNext, loadMore, loading } = useCasinoGamesState();
  const { authenticated } = useApplicationState();
  const { identifyFavoriteGames } = useCasinoNewState();

  useEffect(() => {
    setGamesLoading(loading);
  }, [loading]);

  useEffect(() => {
    if (!authenticated || isEmptyCollection(games)) return;

    const gameIds = games.map(({ id }) => id);

    identifyFavoriteGames(gameIds);
  }, [games, authenticated]);

  const handleLoadMore = useCallback(() => {
    loadMore(currentSlice + 1);
  }, [currentSlice, loadMore]);

  const renderGamesSearchResults = useMemo(() => {
    if (loading && isEmptyCollection(games)) {
      return (
        <div className="casino-search-state">
          <Loader height={48} width={48} />
          <EmptyStateView
            description="casino_search_loading_message"
            title="casino_search_loading_title"
          />
        </div>
      );
    }

    if (isEmptyCollection(games)) {
      return (
        <>
          <div className="casino-search-state">
            <Icon className="error-modal__icon" name="not-found" />
            <EmptyStateView
              description="casino_search_no_results_message"
              title="casino_search_no_results_title"
            />
          </div>
          <CasinoSearchCategoryGames sliderSettings={sliderSettings} />
        </>
      );
    }

    return (
      <div className="casino__games--list-wrapper">
        <HeaderTitle className="sub-text" title="result_found_title" />
        <GamesInfiniteLoader games={games} hasLoadMore={hasNext} loading={loading} />
        {hasNext && <CasinoLoadMoreButton handleClick={handleLoadMore} />}
      </div>
    );
  }, [games, handleLoadMore, hasNext, loading]);

  return renderGamesSearchResults;
};

const SearchResults = ({ className, group, searchQuery, setGamesLoading }) => {
  const { filter } = useCasinoFilterState();

  const renderSearchResults = useMemo(() => {
    if (searchQuery.length < MIN_SEARCH_COUNT) {
      return (
        <>
          {searchQuery.length > 0 && (
            <div className="casino-search-state">
              <Icon className="error-modal__icon" name="error" />
              <EmptyStateView
                description="casino_search_minimum_message"
                title="casino_search_minimum_title"
              />
            </div>
          )}
          <CasinoSearchCategoryGames sliderSettings={sliderSettings} />
        </>
      );
    }

    return (
      <CasinoGamesProvider
        categories={DEFAULT_FILTER_VALUE}
        group={group}
        name={filter.name}
        providers={DEFAULT_FILTER_VALUE}
      >
        <GamesSearchResults setGamesLoading={setGamesLoading} />
      </CasinoGamesProvider>
    );
  }, [filter.name, group, searchQuery.length, setGamesLoading]);

  return (
    <div className={cx('casino-search-results', className)}>
      <CategoryGamesProvider gamesCount={SEARCH_GAMES_COUNT} group={group}>
        {renderSearchResults}
      </CategoryGamesProvider>
    </div>
  );
};

SearchResults.propTypes = {
  className: PropTypes.string,
  group: PropTypes.string,
  searchQuery: PropTypes.string,
  setGamesLoading: PropTypes.func,
};

export default SearchResults;
